import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import createError from 'http-errors';
import { ManifestConversationType } from '../models/manifest-conversation-types';
import { firstValueFrom } from 'rxjs';

export type recallCampaign = {
  fileId: string;
  fileName: string;
  dealershipCode: string;
  startDate: string;
  maxBatch: number;
  userName: string;
  id: string;
  customersCount: number;
  manifestName?: string;
  manifestIds?: string[];
};

type getConversationsReturn = {
  conversations: recallCampaign[];
};

const urlValidate = environment.vulcan.url + 'validate';
const urlMoveToProcess = environment.vulcan.url + 'process';
const urlGetConversations = environment.vulcan.url + 'dealership';
const urlCreateNewManifest = environment.vulcan.url + 'createNewManifest';

const urlKillManifest = environment.dumbledore.url + 'kill-manifest';

const fileContentHEADER = 'data:text/csv;base64,';

export type validationResult = {
  success: boolean;
  errorLines: any[];
  warnings?: { entries: { row: any[]; warnings: { message: string; path: string[] }[] }[]; quantity: number };

  errorMessage: string;
  numberOfErrors: number;
  fileId: string;
};
@Injectable({
  providedIn: 'root',
})
export class UploadCampaignService {
  constructor(private http: HttpClient) {}

  async getConversations(dealershipId: string): Promise<recallCampaign[]> {
    try {
      const finalURL = `${urlGetConversations}/${dealershipId}/conversation`;

      const ret = await firstValueFrom(
        this.http.get<getConversationsReturn>(finalURL, {
          headers: { Authorization: 'allow', 'Content-Type': 'application/json' },
        }),
      );

      return ret.conversations;
    } catch (e) {
      return e.message;
    }
  }

  async uploadData(
    fileId: string,
    dealershipId: string,
    startDate: string,
    maxBatch: number,
    userName: string,
    conversationName: string,
    conversationType: ManifestConversationType,
    manifestName?: string,
  ) {
    try {
      const body = {
        fileId,
        dealershipId,
        startDate,
        maxBatch,
        userName,
        conversationName,
        conversationType,
        manifestName,
      };

      await firstValueFrom(
        this.http.post(urlMoveToProcess, body, {
          headers: { Authorization: 'allow', 'Content-Type': 'application/json' },
        }),
      );

      return '';
    } catch (e) {
      return e.message;
    }
  }
  async validateInput(
    campaignName: string,
    email: string,
    fileName: string,
    fileContents: string,
    dealershipId: string,
    conversationType: ManifestConversationType,
    manifestName?: string,
  ): Promise<validationResult> {
    try {
      const body = {
        data: fileContents.replace(fileContentHEADER, ''),
        filename: fileName,
        returnEmail: email,
        dealershipId,
        conversationName: campaignName,
        conversationType,
        manifestName,
      };

      const returned: any = await firstValueFrom(
        this.http.post(urlValidate, body, { headers: { Authorization: 'allow', 'Content-Type': 'application/json' } }),
      );

      return {
        success: true,
        errorLines: [],
        warnings: returned.warnings || null,
        errorMessage: '',
        numberOfErrors: 0,
        fileId: returned.fileId || '',
      } as validationResult;
    } catch (e) {
      return {
        success: false,
        errorLines: [],
        errorMessage: e.error?.message || `API Returned: ${e.message}`,
        numberOfErrors: 0,
        fileId: '',
      } as validationResult;
    }
  }

  async killManifest(dealershipCode: string, manifestId: string, preview = false): Promise<number> {
    const body = { dealer_code: dealershipCode, manifest_id: manifestId, preview };

    const response = await firstValueFrom(this.http.post<{ conversations_killed: number }>(urlKillManifest, body));

    return response.conversations_killed;
  }

  async createNewManifest(
    marketingSheetName: string,
    description: string,
    dealershipCode: string,
    startDate: string,
    campaignTitle: string,
    followUpMessages: number,
  ) {
    try {
      const body = {
        marketingSheetName,
        description,
        dealershipCode,
        startDate,
        campaignTitle,
        followUpMessages,
      };

      const response = await firstValueFrom(
        this.http.post(urlCreateNewManifest, body, {
          headers: { Authorization: 'allow', 'Content-Type': 'application/json' },
        }),
      );

      return response;
    } catch (e) {
      // Check for specific HTTP error status and throw corresponding http-error
      if (e.response && e.response.status && e.response.data && e.response.data.message) {
        throw createError(e.response.status, e.response.data.message);
      } else {
        // If no specific error found, throw a generic 500 Internal Server Error
        throw createError(500, 'Internal Server Error');
      }
    }
  }
}
